// Typed access to the VERA.exe bridge (exposed by apps/desktop/src/preload.ts).
// In a normal browser, `desktop` is null and desktop-only features show a download prompt.

export type DesktopFolder = { id: string; name: string; path: string; addedAt: string };
export type DesktopEntry = { name: string; path: string; kind: "folder" | "file"; size: number; modified: string; readable: boolean };
export type UpdateStatus = { state: "off" | "idle" | "checking" | "downloading" | "ready" | "error"; current: string; version?: string; percent?: number; error?: string; checkedAt?: string };
export type DesktopAccount = { name: string; username: string; roles: string[]; homeAccountId: string };

export type CodeProject = { root: string; name: string };
export type CodeStatus = { engine: boolean; projects: CodeProject[]; last: string | null; running: boolean; current: (CodeProject & { branch: string | null }) | null };
export type CodeEvent = { t: string; [k: string]: unknown };

export type DesktopBridge = {
  isDesktop: true;
  platform: string;
  version: string;
  /** True only in a demo build (sample people, View as). Never in the team build. */
  demo(): Promise<boolean>;
  /** Live VERA settings from vera.config.json (answers and voice through the control plane). */
  live?(): Promise<{ live: boolean; controlPlaneUrl: string }>;
  auth: {
    configured(): Promise<boolean>;
    getAccount(): Promise<DesktopAccount | null>;
    signIn(): Promise<DesktopAccount>;
    signOut(): Promise<void>;
    getToken(): Promise<string>;
  };
  azure: {
    status(): Promise<boolean>;
    connect(): Promise<boolean>;
    run<T = unknown>(op: string, args?: Record<string, unknown>): Promise<T>;
  };
  github: {
    status(): Promise<{ state: "connected" | "pending" | "off" | "unavailable"; error: string; org: string }>;
    connect(): Promise<{ userCode: string; verificationUri: string }>;
    disconnect(): Promise<void>;
    repos(): Promise<{ name: string; private: boolean; pushed: string; url: string; issues: number; branch: string }[]>;
    pulls(): Promise<{ repo: string; number: number; title: string; author: string; url: string; draft: boolean; updated: string; checks: "passing" | "failing" | "running" | "none" }[]>;
  };
  graph: {
    status(): Promise<Record<string, boolean>>;
    connect(kind: string): Promise<boolean>;
    run<T = unknown>(op: string, args?: Record<string, unknown>): Promise<T>;
  };
  /** VERA's own updates. Older desktop builds don't have it. */
  update?: {
    status(): Promise<UpdateStatus>;
    check(): Promise<UpdateStatus>;
    install(): Promise<boolean>;
  };
  /** Opens a link from an email VERA read and returns the page's text. Older desktop builds don't have it. */
  web?: {
    read(url: string): Promise<{ title: string; url: string; text: string }>;
  };
  folders: {
    /** Development mode only: which sample person's folders to use. */
    useProfile(id: string | null): Promise<void>;
    list(): Promise<DesktopFolder[]>;
    add(): Promise<DesktopFolder | null>;
    remove(id: string): Promise<void>;
    browse(id: string, relPath: string): Promise<DesktopEntry[]>;
    readText(id: string, relPath: string): Promise<{ text: string; truncated: boolean; size: number }>;
    reveal(id: string, relPath: string): Promise<void>;
  };
  /** VERA Code in a side panel, for the VERANeural team. Older desktop builds don't have it. */
  code?: {
    status(): Promise<CodeStatus>;
    addProject(): Promise<{ projects: CodeProject[]; last: string | null } | null>;
    removeProject(root: string): Promise<{ projects: CodeProject[]; last: string | null }>;
    open(root: string): Promise<CodeProject & { branch: string | null }>;
    send(m: Record<string, unknown>): Promise<boolean>;
    stop(): Promise<void>;
    onEvent(fn: (m: CodeEvent) => void): () => void;
  };
  files: {
    saveAs(defaultName: string, data: Uint8Array): Promise<string | null>;
    /** Opens a PDF, Word, Excel or PowerPoint file VERA made in its usual app. Older desktop builds don't have it. */
    open?(name: string, data: Uint8Array): Promise<string>;
  };
};

declare global {
  interface Window {
    veraDesktop?: DesktopBridge;
  }
}

export const desktop: DesktopBridge | null = typeof window !== "undefined" && window.veraDesktop?.isDesktop ? window.veraDesktop : null;
